import { computed, Directive, ElementRef, HostListener, input, Renderer2 } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: 'input[numberInputMask]',
  providers: [
      {
          provide: NG_VALUE_ACCESSOR,
          useExisting: NumberInputMaskDirective,
          multi: true
      }
  ],  
  standalone: true
})
export class NumberInputMaskDirective {

  maxIntegerDigits = input<number | null>(null);
  decimalPoints = input<number>(2);
  maxLength = computed(() => {
    if(this.maxIntegerDigits()) {
      return this.maxIntegerDigits() + this.decimalPoints()    
    } 
     
    return null;
  });
  
  constructor(private el: ElementRef, private renderer: Renderer2) { }

  private value: any;
  // Helper methods from special-number-input directive
  #allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'Clear',
    ',',
    '.'
  ];

  // Implement ControlValueAccessor interface
  // These methods will be called by the form control
  writeValue(value: any): void {
    this.value = value;
    const formattedValue = this.value ? new Intl.NumberFormat('en-US').format(parseFloat(this.value)) : '';
    this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  // Keydown handler for digit limitations
  @HostListener('keydown', ['$event'])
  onKeyDown(e: KeyboardEvent) {
    let inputValue = (e.target as HTMLInputElement).value;
    if (this.#checkAllowedKeys(e)) {
      return;
    }
    if (isNaN(Number(e.key))) {
      e.preventDefault();
    }
    if (this.#checkTextSelection()) {
      return;
    }
    if (this.#checkForMaxLength(inputValue)) {
      e.preventDefault();
    }
  } 
  
  #checkTextSelection() {
    return window.getSelection()!.toString().trim().length > 0;
  }

  #checkAllowedKeys(e: KeyboardEvent): boolean {
    const allKeys = [
      ...this.#allowedKeys,
      this.decimalPoints() > 0 ? '.' : '',
    ];

    return (allKeys.indexOf(e.key) > -1 || (e.key === 'a' && e.ctrlKey) || (e.key === 'a' && e.metaKey) || (e.key === 'v' && e.ctrlKey));
  }



  #checkForMaxLength(inputValue: string): boolean {
    if (this.decimalPoints() > 0) {
      if(inputValue.includes('.')){
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxLength()) {
          return true;
        }
      }
      else {
        if (inputValue.replace('.', '').replace(/\,/g, '').length === this.maxIntegerDigits()) {
          return true;
        }
      }
    } else {
      if (inputValue.replace(/\,/g, '').length === this.maxLength()) {
        return true;
      }
    }
    return false;
  }

  // Listen for input changes
  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    if(!value.endsWith('.') && !value.endsWith('.0') && !value.match('[.][\\d][0]')){
      const unformattedValue = value.replace(/[^0-9.-]/g, ''); // Remove non-numeric characters
      const truncatedValue = this.#truncateToDecimalPlaces(unformattedValue);
      this.value = truncatedValue;
      this.onChange(this.value);

      const formattedValue = this.value ? new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(this.value) : '';

      this.renderer.setProperty(this.el.nativeElement, 'value', formattedValue);
    }
  }

  #truncateToDecimalPlaces(value: string): string {
    if (!value) {
      return value;
    }
    else if (!value.includes('.')) {
      return value;
    }
    
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toFixed(this.decimalPoints());
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

}
